/**
 * ซิงก์ความคืบหน้าข้ามเครื่อง
 *
 * ตัวเก็บกลางเป็นแค่ที่วางไฟล์ — การรวมข้อมูลทำที่เครื่องเรา เพราะ importBackup()
 * ตัดสินทีละใบด้วย updatedAt อยู่แล้ว จึงไม่ต้องมี logic ซ้ำอีกฝั่ง
 *
 * ลำดับ: ดึงของกลาง → รวมเข้าเครื่อง → ส่งผลรวมกลับขึ้นไป
 * ถ้าระหว่างนั้นมีเครื่องอื่นเขียนแทรก จะได้ 409 แล้ววนใหม่ ไม่ทับของเขา
 */
import { exportBackup, importBackup, isBackup, type Backup } from './backup'

export interface SyncConfig {
  url: string
  key: string
}

export interface SyncResult {
  pulled: number
  pushed: number
  version: number
  at: number
}

/** ครั้งเดียวก็พอสำหรับคนใช้คนเดียว — ชนสองรอบติดแปลว่ามีอย่างอื่นผิด */
const MAX_RETRY = 2

function endpoint(cfg: SyncConfig): string {
  return `${cfg.url.replace(/\/+$/, '')}/state`
}

function headers(cfg: SyncConfig): HeadersInit {
  return { authorization: `Bearer ${cfg.key}`, 'content-type': 'application/json' }
}

async function pull(cfg: SyncConfig): Promise<{ version: number; data: Backup | null }> {
  const res = await fetch(endpoint(cfg), { headers: headers(cfg) })
  if (res.status === 401) throw new Error('รหัสไม่ถูก — ตรวจ SYNC_KEY อีกที')
  if (!res.ok) throw new Error(`ดึงข้อมูลไม่สำเร็จ (${res.status})`)

  const body = (await res.json()) as { version?: number; data?: unknown }
  const version = typeof body.version === 'number' ? body.version : 0
  return { version, data: isBackup(body.data) ? body.data : null }
}

async function push(
  cfg: SyncConfig,
  version: number,
  data: Backup,
): Promise<{ ok: true; version: number } | { ok: false; version: number }> {
  const res = await fetch(endpoint(cfg), {
    method: 'PUT',
    headers: headers(cfg),
    body: JSON.stringify({ version, data }),
  })

  if (res.status === 409) {
    const body = (await res.json()) as { version?: number }
    return { ok: false, version: typeof body.version === 'number' ? body.version : version }
  }
  if (!res.ok) throw new Error(`ส่งข้อมูลไม่สำเร็จ (${res.status})`)

  const body = (await res.json()) as { version?: number }
  return { ok: true, version: typeof body.version === 'number' ? body.version : version + 1 }
}

export async function syncNow(cfg: SyncConfig): Promise<SyncResult> {
  if (!cfg.url || !cfg.key) throw new Error('ยังไม่ได้ตั้งค่าที่อยู่กับรหัส')

  for (let attempt = 0; attempt < MAX_RETRY; attempt++) {
    const remote = await pull(cfg)

    // รวมของกลางเข้าเครื่องก่อน แล้วค่อยส่งผลรวมกลับ — ทำสลับกันจะทำให้ของเครื่องอื่นหาย
    const merged = remote.data ? await importBackup(remote.data) : { progress: 0, drill: 0 }
    const local = await exportBackup()

    const result = await push(cfg, remote.version, local)
    if (result.ok) {
      return {
        pulled: merged.progress + merged.drill,
        pushed: local.progress.length + local.drill.length,
        version: result.version,
        at: Date.now(),
      }
    }
    // เครื่องอื่นเขียนแทรกระหว่างเรากำลังรวม — เริ่มใหม่จากของล่าสุด
  }

  throw new Error('มีเครื่องอื่นเขียนแทรกอยู่ ลองใหม่อีกครั้ง')
}

/** ทดสอบว่าที่อยู่กับรหัสใช้ได้ไหม โดยไม่แตะข้อมูล */
export async function testConnection(cfg: SyncConfig): Promise<number> {
  const { version } = await pull(cfg)
  return version
}
